import { toRefs, reactive, watch, computed } from 'vue'
import GeoJSON from 'geojson'
import mapboxgl from 'mapbox-gl'
import { useQuery, provideApolloClient } from '@vue/apollo-composable'
import gql from 'graphql-tag'

const state = reactive({
  mapObj: null,
  federalStates: [],
  buildingCategories: [],
  cities: [],
  maxParcelArea: 2000,
  showScoreBoard: false,
  showFilters: false,
  showLeadListPopup: false,
  mapBoxFeaturesLoaded: false,
  showVideoPopup: false,
  query: {
    federalState: [],
    city: [],
    buildingCategory: [],
    parcelArea: { min: 0, max: 10000 },
    renovationPressure: { min: 0, max: 1 },
    maxVolume: { min: 0, max: 10000 },
    sortBy: 'parcel.address',
    descending: true
  },
  mapStyle: 'LIGHT',
  selectedSearchResult: { addressId: null, address: null },
  searchText: process.env.NODE_ENV === 'production' ? null : 'Buchenweg 4 9043 Trogen',
  showPopup: null,
  addressInvalid: false,
  showQuickView: false,
  marker: false,
  findPotentialEnabled: true,
  searchAddressesEnabled: true,
  selectedPotential: null,
  markerLayerLoaded: false,
  allLeadsEnabled: false,
  filtersLoaded: false,
  leads: [],
  mapLoading: false,
  showInvalidPotentialPopup: false,
  invalidPotentialBuildingCategory: null,
  invalidPotentialBuildingClass: null,
  maxMaxVolume: 10000,
  searchResults: [],
  showMaxNumberOfReportsReached: false,
  userAuthenticated: false,
  sharedApolloClient: null,
  showMailNotificationPopup: false,
  widgetQuestionnaireForm: null,
  analyzerLoaded: false,
  analyzerType: 'B2B'
})

watch(() => state.sharedApolloClient, value => {
  provideApolloClient(state.sharedApolloClient)
})

const { refetch: getLeads, onResult, onError: onLeadsError, loading: leadsLoading } = useQuery(
  gql`
    query allLeads ($leadsInput: LeadsInput!) {
      allLeads(leadsInput: $leadsInput) {
        _id,
        potential {
          _id,
          lat,
          long,
          hasLead,
          buildingZones {
            squareMeters
            buildingZone {
              shortName
              zoneType
            }
          }
          yearOfConstructionEndMax,
          city {
            name
            buildingRegulationsPage
          }
          parcel {
            area,
            parcelNr
            builtUpArea
            maxVolume
            currentVolume
            roofStats {
              flatRoofShare
            }
            geometryCoordinates {
              lat
              lon
            }
            renovationPressure
          }
          buildings {
            address,
            town,
            buildingCategory
          }
          analysis {
            score
            potentialVolume
            isBuildingZoneBased
          }
          oerebLink
        }
      }
    }
  `,
  { leadsInput: {} },
  () => { return { enabled: state.userAuthenticated && state.filtersLoaded, metadata: false, debounce: 500 } }
)
onLeadsError(v => { console.log('GET LEADS ERROR', JSON.parse(JSON.stringify(v))) })
onResult(v => {
  if (v && v.data && v.data.allLeads) {
    state.leads = v.data.allLeads
  }
  state.mapLoading = false
})

const { result: citiesResult, refetch: getCities, error: citiesError } = useQuery(
  gql`
    query ($citiesInput: CitiesInput!) {
      allCities (citiesInput: $citiesInput) {
        _id
        name
      }
    }
  `,
  () => {
    return {
      citiesInput: {
        federalStates: state.query.federalState
      }
    }
  },
  () => { return { enabled: state.userAuthenticated } }
)
watch(citiesError, () => { console.log('GET CITIES ERROR', JSON.parse(JSON.stringify(citiesError.value))) })
watch(citiesResult, (result) => {
  if (result && result.allCities) {
    state.cities = result.allCities
  } else {
    console.log('FAILED LOADED CITIES', result)
  }
})

const { result: filtersResult, refetch: getFilters, error: filtersError } = useQuery(
  gql`
    query {
      localizerFilters {
        maxParcelArea,
        maxMaxVolume
        buildingCategories {
          _id
        },
        federalStates {
          _id
          shortName
          fullName
        }
      }
    }
  `,
  null,
  () => { return { enabled: state.userAuthenticated } }
)
watch(filtersError, () => { console.log('GET FILTERS ERROR', JSON.parse(JSON.stringify(filtersError.value))) })
watch(filtersResult, (result) => {
  if (result && result.localizerFilters) {
    state.federalStates = result.localizerFilters.federalStates
    state.maxParcelArea = result.localizerFilters.maxParcelArea
    state.maxMaxVolume = result.localizerFilters.maxMaxVolume
    state.buildingCategories = result.localizerFilters.buildingCategories
    state.query.parcelArea.max = result.localizerFilters.maxParcelArea
    state.query.maxVolume.max = result.localizerFilters.maxMaxVolume
    state.filtersLoaded = true
  } else {
    console.log('FAILED LOADING FILTERS', result)
  }
})

const loadMapBoxFeatures = (keepPosition) => {
  try {
    removeAllMapboxLayersAndSources()
    if (!keepPosition && state.showQuickView === false) {
      state.mapObj.flyTo({ center: [8.5417, 47.3769], zoom: 7 })
    }
    const data = GeoJSON.parse(state.leads, { Point: ['potential.lat', 'potential.long'] })
    state.mapObj.addSource('leads', { cluster: true, clusterMaxZoom: 13, type: 'geojson', data })
    state.mapObj.addSource('clusterLeads', { cluster: true, clusterMaxZoom: 10, clusterRadius: 5, type: 'geojson', data })
    const leadsWithGeometryCoordinates = state.leads.filter(lead => lead.potential.parcel.geometryCoordinates)
    if (leadsWithGeometryCoordinates && leadsWithGeometryCoordinates.length > 0) {
      const geoJsonLeads = {
        type: 'FeatureCollection',
        features: leadsWithGeometryCoordinates.map(lead => {
          return {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: lead.potential.parcel.geometryCoordinates.map(e => [e.lat, e.lon])
            },
            properties: lead
          }
        })
      }

      state.mapObj.addSource('geometryCoordinates', { type: 'geojson', data: geoJsonLeads })
      state.mapObj.addLayer({ id: 'outline', minzoom: 14, maxzoom: 24, type: 'line', source: 'geometryCoordinates', layout: {}, paint: { 'line-color': '#5FEFC0', 'line-width': 2 } })
      state.mapObj.addLayer({ id: 'fillPotentials', type: 'fill', source: 'geometryCoordinates', layout: {}, paint: { 'fill-color': 'rgba(95, 239, 192, 0.4)', 'fill-opacity': 0.5 } })
    }

    state.mapObj.addLayer({ id: 'clusters', minzoom: 10, maxzoom: 13, type: 'circle', source: 'leads', paint: { 'circle-radius': { property: 'dbh', type: 'exponential', stops: [[{ zoom: 15, value: 1 }, 5], [{ zoom: 15, value: 62 }, 12], [{ zoom: 22, value: 1 }, 20], [{ zoom: 22, value: 62 }, 50]] }, 'circle-color': 'rgb(95, 239, 192)', 'circle-stroke-color': 'rgba(95, 239, 192, 0.3)', 'circle-stroke-width': 30, 'circle-opacity': 1 } })
    state.mapObj.addLayer({ id: 'clusters-count', minzoom: 10, maxzoom: 13, type: 'symbol', source: 'leads', filter: ['has', 'point_count'], layout: { 'text-field': '{point_count_abbreviated}', 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'], 'text-size': 12 } })
    state.mapObj.addLayer({
      id: 'heatmap',
      maxzoom: 10,
      type: 'heatmap',
      source: 'clusterLeads',
      paint: {
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0, 'rgba(236,222,239,0.1)',
          0.1, 'rgba(83,227,180,0.2)',
          0.3, 'rgba(83,227,180,0.3)',
          0.4, 'rgba(83,227,180,0.9)',
          0.5, 'rgba(83,227,180,0.6)'
        ],
        'heatmap-radius': { stops: [[10, 40], [11, 30], [12, 20], [13, 10]] },
        'heatmap-opacity': { default: 1, stops: [[14, 0.8], [15, 0.6]] }
      }
    })

    state.mapObj.on('click', 'markers', (e) => {
      try {
        state.mapObj.flyTo({ center: e.features[0].geometry.coordinates })
        state.selectedPotential = JSON.parse(e.features[0].properties.potential)
        state.showQuickView = true
      } catch (e) {
        console.log(e)
      }
    })
    state.mapObj.on('click', 'fillPotentials', (e) => {
      const lead = {
        ...e.features[0].properties,
        potential: JSON.parse(e.features[0].properties.potential)
      }
      if (lead && lead.potential) {
        state.mapObj.flyTo({ center: [lead.potential.long, lead.potential.lat], zoom: state.mapObj.getZoom() + 1, bearing: 0, speed: 1, curve: 1, essential: true })
        state.selectedPotential = lead.potential
        state.showQuickView = true
      }
    })
    state.mapBoxFeaturesLoaded = true
  } catch (e) {
    console.log('iERROR LOADING MAPBOX FEATURES', e)
  }
}

const removeAllMapboxLayersAndSources = () => {
  try {
    if (state.mapBoxFeaturesLoaded) {
      try {
        if (state.mapObj.getStyle().layers.some(e => e.id === 'clusters')) { state.mapObj.removeLayer('clusters') }
        if (state.mapObj.getStyle().layers.some(e => e.id === 'clusters-count')) { state.mapObj.removeLayer('clusters-count') }
        if (state.mapObj.getStyle().layers.some(e => e.id === 'heatmap')) { state.mapObj.removeLayer('heatmap') }
        if (state.mapObj.getStyle().layers.some(e => e.id === 'outline')) { state.mapObj.removeLayer('outline') }
        if (state.mapObj.getStyle().layers.some(e => e.id === 'fillPotentials')) { state.mapObj.removeLayer('fillPotentials') }
        if (state.mapObj.getStyle().layers.some(e => e.id === 'highlightedSelectedPotential')) { state.mapObj.removeLayer('highlightedSelectedPotential') }
        if (state.mapObj.getStyle().layers.some(e => e.id === 'fillSelectedPotential')) { state.mapObj.removeLayer('fillSelectedPotential') }
        if (state.mapObj.getStyle().sources.geometryCoordinates) { state.mapObj.removeSource('geometryCoordinates') }
        if (state.mapObj.getStyle().sources.selectedPotentialCoordinates) { state.mapObj.removeSource('selectedPotentialCoordinates') }
        if (state.mapObj.getStyle().sources.leads) { state.mapObj.removeSource('leads') }
        if (state.mapObj.getStyle().sources.clusterLeads) { state.mapObj.removeSource('clusterLeads') }
        state.mapBoxFeaturesLoaded = false
      } catch (e) {
        console.log('Not all layers were initialized', e)
      }
    }
  } catch (e) {
    console.log('not all layer existed')
  }
}

export const resetFilters = () => {
  Object.assign(state.query, {
    federalState: [],
    city: [],
    buildingCategory: [],
    parcelArea: { min: 0, max: state.maxParcelArea },
    renovationPressure: { min: 0, max: 1 },
    maxVolume: { min: 0, max: state.maxMaxVolume },
    sortBy: 'parcel.address',
    descending: true
  })
  state.mapLoading = true
  getLeads({ leadsInput: { } })
  setTimeout(() => {
    state.mapLoading = false
  }, 500)
}

function loadMarker (addMarker, screen, mapboxSettings) {
  if (!state.selectedPotential) {
    return
  }

  if (state.marker) {
    state.marker.remove()
    state.marker = null
  }
  const lnglat = [state.selectedPotential.long, state.selectedPotential.lat]
  if (state.selectedPotential.parcel.geometryCoordinates && state.selectedPotential.parcel.geometryCoordinates.length > 0) {
    if (state.markerLayerLoaded) {
      state.mapObj.removeLayer('highlightedSelectedPotential')
      state.mapObj.removeLayer('fillSelectedPotential')
      state.mapObj.removeSource('selectedPotentialCoordinates')
      state.markerLayerLoaded = false
    }
    state.mapObj.addSource('selectedPotentialCoordinates', { type: 'geojson', data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [state.selectedPotential.parcel.geometryCoordinates.map(e => [e.lat, e.lon])] } } })
    state.mapObj.addLayer({ id: 'highlightedSelectedPotential', minzoom: 1, maxzoom: 24, type: 'line', source: 'selectedPotentialCoordinates', layout: {}, paint: { 'line-color': mapboxSettings.outlineColor || '#5FEFC0', 'line-width': mapboxSettings.outlineLineWidth || 2 } })
    state.mapObj.addLayer({ id: 'fillSelectedPotential', type: 'fill', source: 'selectedPotentialCoordinates', layout: {}, paint: { 'fill-color': mapboxSettings.fillColor || 'rgba(95, 239, 192, 0.4)', 'fill-opacity': mapboxSettings.fillOpacity || 0.5 } })
  }
  let offset
  if (screen && screen.gt && screen.gt.xs) {
    offset = [-300, 0]
  } else {
    if (state.analyzerType === 'B2B') {
      offset = [0, -100]
    } else {
      offset = [0, -150]
    }
  }
  state.mapObj.flyTo({ center: lnglat, zoom: 17, curve: 1, essential: true, offset })
  if (state.mapObj.getZoom() === 17) {
    addMarkerToHighLighting(lnglat, addMarker)
    state.showQuickView = true
  } else {
    state.mapObj.once('zoomend', () => {
      if (state.selectedPotential) {
        addMarkerToHighLighting(lnglat, addMarker)
        state.showQuickView = true
      }
    })
  }
}

function addMarkerToHighLighting (lnglat, addMarker) {
  if (addMarker) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.innerHTML = '<path d="M7 0C3.13 0 0 3.13 0 7C0 12.25 7 20 7 20C7 20 14 12.25 14 7C14 3.13 10.87 0 7 0ZM7 9.5C5.62 9.5 4.5 8.38 4.5 7C4.5 5.62 5.62 4.5 7 4.5C8.38 4.5 9.5 5.62 9.5 7C9.5 8.38 8.38 9.5 7 9.5Z" fill="#22223C"/>'
    state.marker = new mapboxgl.Marker(svg).setLngLat(lnglat).addTo(state.mapObj)
    state.markerLayerLoaded = true
  }
  state.markerLayerLoaded = true
}

export function useAnalyzer () {
  const scoreBoardLeads = computed(() => {
    return state.leads.filter(e => e.potential.parcel && e.potential.parcel.renovationPressure && e.potential.analysis && e.potential.analysis.potentialVolume)
  })
  return {
    ...toRefs(state),
    scoreBoardLeads,
    removeAllMapboxLayersAndSources,
    loadMapBoxFeatures,
    resetFilters,
    getLeads,
    leadsLoading,
    getFilters,
    getCities,
    loadMarker
  }
}
